/**
 * The entity types supported by the Favourites API.
 * Mirrors the backend `FavouriteEntityType` enum.
 */
export const FAVOURITE_ENTITY_TYPES = {
  PROFESSIONAL: "PROFESSIONAL",
  QUESTION: "QUESTION",
  ANSWER: "ANSWER",
  INSPIRATION: "INSPIRATION",
  INSPIRATION_PHOTO: "INSPIRATION_PHOTO",
  PROFESSION_TASK: "PROFESSION_TASK",
  PROFESSION: "PROFESSION",
  BLOG_ARTICLE: "BLOG_ARTICLE",
};

export const FAVOURITE_ENTITY_TYPE_LIST = Object.values(FAVOURITE_ENTITY_TYPES);

/**
 * The union members share `name`/`title`/`seoName` field names with conflicting
 * nullability, so the GraphQL fragment aliases them per `__typename`
 * (see `graphql/fragments/favouriteEntityFragment.graphql`). These helpers read
 * the right aliased key back out, keyed by the entity's `__typename`.
 */
const LABEL_KEY_BY_TYPENAME = {
  Person: "personName",
  NewPerson: "personName",
  Question: "questionTitle",
  Inspiration: "inspirationName",
  InspirationPhoto: "photoTitle",
  ProfessionTask: "taskName",
  Profession: "professionName",
  BlogArticle: "articleTitle",
};

const SEO_NAME_KEY_BY_TYPENAME = {
  Person: "personSeoName",
  NewPerson: "personSeoName",
  Question: "questionSeoName",
  Inspiration: "inspirationSeoName",
  ProfessionTask: "taskSeoName",
  Profession: "professionSeoName",
};

export function favouriteEntityLabel(entity) {
  if (!entity) {
    return "";
  }
  // Answers have no title of their own; label them by the question they answer.
  if (entity.__typename === "Answer") {
    return entity.question?.answerQuestionTitle || "";
  }
  const key = LABEL_KEY_BY_TYPENAME[entity.__typename];
  return (key && entity[key]) || "";
}

export function favouriteEntitySeoName(entity) {
  if (!entity) {
    return null;
  }
  const key = SEO_NAME_KEY_BY_TYPENAME[entity.__typename];
  return (key && entity[key]) || null;
}

const personRoute = (e) => ({
  name: "professionalPage",
  params: { professionalSeoName: e.personSeoName },
});

/**
 * Maps a favourited entity to the route of the page it represents (so links go
 * to the entity's own page, not the statistics-only stored `url`). Returns a
 * `{ name, params }` route descriptor for `localePath`, or `null` when the
 * entity's own page can't be resolved from the available fields (e.g.
 * inspiration photos, whose parent inspiration seoName isn't exposed) — callers
 * should fall back to the stored url in that case.
 */
const ROUTE_BY_TYPENAME = {
  Person: personRoute,
  NewPerson: personRoute,
  Question: (e) => ({
    name: "questionPage",
    params: { questionSeoName: e.questionSeoName },
  }),
  // Answers have no page of their own; link to the question they answer.
  Answer: (e) => ({
    name: "questionPage",
    params: { questionSeoName: e.question?.answerQuestionSeoName },
  }),
  Inspiration: (e) => ({
    name: "inspirationPage",
    params: { inspirationSeoName: e.inspirationSeoName },
  }),
  InspirationPhoto: (e) => ({
    name: "inspirationPicturePage",
    params: {
      inspirationSeoName: e.inspiration?.photoInspirationSeoName,
      pictureSeoName: e.photoSeoName,
    },
  }),
  Profession: (e) => ({
    name: "professionPage",
    params: { profession: e.professionSeoName },
  }),
  ProfessionTask: (e) => ({
    name: "prices",
    params: { professionTask: e.taskSeoName },
  }),
  BlogArticle: (e) => ({
    name: "blogArticlePage",
    params: { articleSeoName: e.articleSeoTitle },
  }),
};

export function favouriteEntityRoute(entity) {
  const builder = entity && ROUTE_BY_TYPENAME[entity.__typename];
  const route = builder ? builder(entity) : null;
  // Guard against missing seoName values producing a broken link.
  if (!route || Object.values(route.params).some((v) => !v)) {
    return null;
  }
  return route;
}
